import { QuestionPriority, QuestionType } from "../graphql/generated";

export type SectionQuestion = {
  id: string;
  title: string;
  type?: QuestionType | null;
  index: string;
  priority?: QuestionPriority | null;
  answers?: Array<{ id: string } | null> | null;
};

export type Section = {
  id: string;
  name: string;
  sortOrder: number;
  questions: SectionQuestion[];
};

export const PRIORITY_WEIGHTS: Record<QuestionPriority, number> = {
  [QuestionPriority.Urgent]: 5,
  [QuestionPriority.High]: 5,
  [QuestionPriority.Medium]: 2,
  [QuestionPriority.Low]: 1,
  [QuestionPriority.None]: 1,
};

export function computeWeightedDdqlRatio(sections: Section[]): number {
  let weightedAnswered = 0;
  let weightedTotal = 0;
  for (const section of sections) {
    for (const q of section.questions) {
      const w = PRIORITY_WEIGHTS[q.priority ?? QuestionPriority.None] ?? 1;
      weightedTotal += w;
      if ((q.answers?.length ?? 0) > 0) weightedAnswered += w;
    }
  }
  return weightedTotal > 0 ? weightedAnswered / weightedTotal : 0;
}

export function computeHighPriorityRatio(sections: Section[]): number {
  let answered = 0;
  let total = 0;
  for (const section of sections) {
    for (const q of section.questions) {
      if (
        q.priority === QuestionPriority.High ||
        q.priority === QuestionPriority.Urgent
      ) {
        total += 1;
        if ((q.answers?.length ?? 0) > 0) answered += 1;
      }
    }
  }
  return total > 0 ? answered / total : 1;
}

export function computeReadinessScore({
  ddqlRatio,
  highPriorityRatio,
  valuation,
  ebitda,
  industry,
}: {
  ddqlRatio: number;
  highPriorityRatio: number;
  valuation: { output: { mid: number } } | null | undefined;
  ebitda: number | null;
  industry: { multipleLow: number; multipleHigh: number } | null | undefined;
}): number {
  const clamp = (v: number, min: number, max: number) =>
    Math.max(min, Math.min(max, v));

  const ddqlScore = ddqlRatio * 50;

  let valuationScore = 0;
  if (valuation && ebitda && ebitda > 0 && industry) {
    const dealMid = valuation.output.mid / ebitda;
    const range = industry.multipleHigh - industry.multipleLow;
    if (range > 0) {
      const ratio = clamp(
        (dealMid - industry.multipleLow) / range,
        0,
        1.1,
      );
      valuationScore = highPriorityRatio * ratio * 50;
    }
  } else if (valuation && !industry) {
    valuationScore = highPriorityRatio * 0.5 * 50;
  }

  return clamp(Math.round(ddqlScore + valuationScore), 5, 99);
}

export function readinessLabel(score: number): string {
  if (score < 20) return "NOT READY";
  if (score < 40) return "EARLY STAGE";
  if (score < 60) return "IN PROGRESS";
  if (score < 76) return "NEAR READY";
  if (score < 91) return "ALMOST READY";
  return "READY";
}

export const fmtM = (n: number) => `£${(n / 1_000_000).toFixed(2)}M`;

export const fmt = (n: number) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    maximumFractionDigits: 0,
  }).format(n);
